import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AnimatePresence } from 'motion/react';
import { AppLayout } from '@/components/layout/AppLayout/index.js';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute/index.js';
import { LoadingSpinner } from '@/components/common/LoadingSpinner/LoadingSpinner.js';
import { PageTransition } from '@/components/motion/PageTransition.js';
import {
  loadBrandingSettingsPage,
  loadCalculatorIndexPage,
  loadCalculatorPage,
  loadChatPage,
  loadDashboardPage,
  loadMaterialsPage,
  loadProfilePage,
  loadProjectCreatePage,
  loadProjectDetailPage,
  loadProjectListPage,
  loadRatesPage,
  loadTaskBoardPage,
  loadTaskSelectPage,
  loadTeamDetailPage,
  loadTeamsPage,
  loadTemplateDetailPage,
  loadTemplatesIndexPage,
} from './routeModules.js';

const LoginPage = lazy(() => import('@/pages/auth/LoginPage/LoginPage.js'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage/RegisterPage.js'));
const VerifyEmailPage = lazy(() => import('@/pages/auth/VerifyEmailPage/VerifyEmailPage.js'));
const DashboardPage = lazy(loadDashboardPage);
const ProfilePage = lazy(loadProfilePage);
const ProjectListPage = lazy(loadProjectListPage);
const ProjectCreatePage = lazy(loadProjectCreatePage);
const ProjectDetailPage = lazy(loadProjectDetailPage);
const RatesPage = lazy(loadRatesPage);
const MaterialsPage = lazy(loadMaterialsPage);
const ChatPage = lazy(loadChatPage);
const TaskSelectPage = lazy(loadTaskSelectPage);
const TaskBoardPage = lazy(loadTaskBoardPage);
const TeamsPage = lazy(loadTeamsPage);
const TeamDetailPage = lazy(loadTeamDetailPage);
const CallPage = lazy(() => import('@/pages/calls/CallPage/index.js'));
const TemplatesIndexPage = lazy(loadTemplatesIndexPage);
const TemplateDetailPage = lazy(loadTemplateDetailPage);
const BrandingSettingsPage = lazy(loadBrandingSettingsPage);
const CalculatorIndexPage = lazy(loadCalculatorIndexPage);
const CalculatorPage = lazy(loadCalculatorPage);

function PageLoader() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  );
}

/**
 * Wraps lazy page in a PageTransition motion wrapper. The key replays the enter
 * animation per section; param-only navigations (/chat/abc → /chat/xyz) keep it.
 */
function withTransition(node: React.ReactNode, key?: string) {
  return <PageTransition key={key}>{node}</PageTransition>;
}

const PUBLIC_PATHS = new Set(['/login', '/register', '/verify-email']);

/**
 * Все страницы внутри AppLayout делят один ключ, поэтому смена раздела не пересоздаёт
 * раскладку с сайдбаром. React Router меняет адрес в startTransition: пока грузится
 * чанк нового раздела, на экране остаётся текущий. Раньше ключ был у каждого раздела,
 * AnimatePresence монтировал новую страницу уже вне перехода, и корневой Suspense
 * прятал весь экран вместе с сайдбаром.
 */
function getTopLevelKey(pathname: string): string {
  if (PUBLIC_PATHS.has(pathname)) return pathname;
  if (pathname.startsWith('/call/')) return 'call';
  return 'app';
}

export function AppRouter() {
  const location = useLocation();

  // Без initial={false}: PresenceChild запоминает его навсегда, и у всех страниц,
  // смонтированных позже внутри «app», блокируется начальная анимация. Родитель со
  // stagger не запускал «show», и карточки проектов оставались невидимыми до hover.
  return (
    <Suspense fallback={<PageLoader />}>
      <AnimatePresence mode="wait">
        <Routes location={location} key={getTopLevelKey(location.pathname)}>
          {/* Public routes */}
          <Route path="/login" element={withTransition(<LoginPage />)} />
          <Route path="/register" element={withTransition(<RegisterPage />)} />
          <Route path="/verify-email" element={withTransition(<VerifyEmailPage />)} />

          {/* Protected routes with layout */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={withTransition(<DashboardPage />, 'dashboard')} />
            <Route path="/profile" element={withTransition(<ProfilePage />, 'profile')} />
            <Route path="/projects" element={withTransition(<ProjectListPage />, 'projects')} />
            <Route
              path="/projects/new"
              element={withTransition(<ProjectCreatePage />, 'projects/new')}
            />
            <Route
              path="/projects/:id"
              element={withTransition(<ProjectDetailPage />, 'projects/:id')}
            />
            <Route path="/rates" element={withTransition(<RatesPage />, 'rates')} />
            <Route path="/materials" element={withTransition(<MaterialsPage />, 'materials')} />
            <Route path="/chat" element={withTransition(<ChatPage />, 'chat')} />
            <Route path="/chat/:conversationId" element={withTransition(<ChatPage />, 'chat')} />
            <Route path="/tasks" element={withTransition(<TaskSelectPage />, 'tasks')} />
            <Route
              path="/tasks/:projectId"
              element={withTransition(<TaskBoardPage />, 'tasks/:projectId')}
            />
            <Route path="/teams" element={withTransition(<TeamsPage />, 'teams')} />
            <Route path="/teams/:id" element={withTransition(<TeamDetailPage />, 'teams/:id')} />
            <Route
              path="/templates"
              element={withTransition(<TemplatesIndexPage />, 'templates')}
            />
            <Route
              path="/templates/:id"
              element={withTransition(<TemplateDetailPage />, 'templates/:id')}
            />
            <Route
              path="/settings/branding"
              element={withTransition(<BrandingSettingsPage />, 'settings/branding')}
            />
            <Route
              path="/calculator"
              element={withTransition(<CalculatorIndexPage />, 'calculator')}
            />
            <Route
              path="/calculator/:slug"
              element={withTransition(<CalculatorPage />, 'calculator/:slug')}
            />
          </Route>

          {/* Fullscreen call page — protected but outside AppLayout (no sidebar/header) */}
          <Route
            path="/call/:callId"
            element={
              <ProtectedRoute>
                <CallPage />
              </ProtectedRoute>
            }
          />

          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AnimatePresence>
    </Suspense>
  );
}
